import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Card, Row, Col, Upload, Space } from 'antd';
import { PlusOutlined, UploadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { getSkills, imagepath } from '../api';

const { Option } = Select;
const documentTypes = [
  { value: 'passport', label: 'Passport' },
  { value: 'visa', label: 'Visa' },
  { value: 'emirates_id', label: 'Emirates ID' },
  { value: 'labour_card', label: 'Labour Card' },
  { value: 'driving_licence', label: 'Driving Licence' },
  { value: 'health_insurance', label: 'Health Insurance' },
  { value: 'workmen_insurance', label: 'Workmen Insurance' },
];

const SubcontractorEmployeeForm = ({ initialValues, onSubmit, loading, mode = 'add', onCancel }) => {
  const [form] = Form.useForm();
  const [imagePreview, setImagePreview] = useState(null);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    getSkills().then(res => {
      if (res.success) setSkills(res.data);
    });

    if (initialValues) {
      const formattedValues = { ...initialValues };
      if (formattedValues.documents && formattedValues.documents.length > 0) {
        formattedValues.documents = formattedValues.documents.map(doc => ({
          ...doc,
          document_file: doc.document_file ? [{
            uid: doc.id,
            name: doc.original_filename || (typeof doc.document_file === 'string' ? doc.document_file.split('/').pop() : 'document'),
            status: 'done',
            url: doc.document_file.startsWith('http') ? doc.document_file : `${imagepath}${doc.document_file}`,
          }] : []
        }));
      }

      if (formattedValues.profile_image && typeof formattedValues.profile_image === 'string') {
        formattedValues.profile_image = [{
          uid: '-1',
          name: formattedValues.profile_image.split('/').pop(),
          status: 'done',
          url: `${imagepath}${formattedValues.profile_image}`,
        }];
      } else if (!Array.isArray(formattedValues.profile_image)) {
        formattedValues.profile_image = [];
      }

      form.setFieldsValue(formattedValues);

      if (formattedValues.profile_image && formattedValues.profile_image.length > 0) {
        setImagePreview(formattedValues.profile_image[0].url);
      }
    }
  }, [initialValues, form]);

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: '#fff', padding: '40px 5vw' }}>
      <h2>{mode === 'edit' ? 'Edit Subcontractor Employee' : 'Add Subcontractor Employee'}</h2>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Row gutter={24}>
          <Col span={12}>
            <Card title="Personal Information" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="emp_code" label="Employee Code"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="email" label="Email" rules={[{ type: 'email' }]}><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="phone" label="Phone (UAE)"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="native_phone" label="Native Phone (Home Country)"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="dob" label="Date of Birth"><Input type="date" /></Form.Item></Col>
                <Col span={12}><Form.Item name="address" label="Address"><Input /></Form.Item></Col>
                <Col span={12}>
                  <Form.Item
                    name="profile_image"
                    label="Profile Image"
                    valuePropName="fileList"
                    initialValue={[]}
                    getValueFromEvent={e => {
                      if (Array.isArray(e)) return e;
                      return e && e.fileList;
                    }}
                  >
                    <Upload listType="picture" maxCount={1} beforeUpload={() => false}>
                      <Button icon={<UploadOutlined />}>Select Image</Button>
                    </Upload>
                  </Form.Item>
                  {imagePreview && <img src={imagePreview} alt="Preview" style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }} />}
                </Col>
              </Row>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Job Information" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col span={12}><Form.Item name="position" label="Position"><Input /></Form.Item></Col>
                <Col span={12}><Form.Item name="skill_id" label="Skill"><Select allowClear>{skills.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}</Select></Form.Item></Col>
                <Col span={12}><Form.Item name="joining_date" label="Joining Date"><Input type="date" /></Form.Item></Col>
                <Col span={12}><Form.Item name="status" label="Status" rules={[{ required: true }]}><Select><Option value="active">Active</Option><Option value="inactive">Inactive</Option></Select></Form.Item></Col>
                <Col span={12}><Form.Item name="salary_type" label="Salary Type"><Select><Option value="monthly">Monthly</Option><Option value="hourly">Hourly</Option></Select></Form.Item></Col>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.salary_type !== curr.salary_type}>
                  {({ getFieldValue }) => getFieldValue('salary_type') === 'monthly' ? (
                    <Col span={12}><Form.Item name="base_salary" label="Base Salary"><Input type="number" /></Form.Item></Col>
                  ) : getFieldValue('salary_type') === 'hourly' ? (
                    <Col span={12}><Form.Item name="hourly_rate" label="Hourly Rate"><Input type="number" /></Form.Item></Col>
                  ) : null}
                </Form.Item>
                <Col span={12}><Form.Item name="over_time_rate" label="Overtime Rate"><Input type="number" /></Form.Item></Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Card title="Employee Documents" style={{ marginBottom: 24 }}>
          <Form.List name="documents">
            {(fields, { add, remove }) => (
              <div key="documents-container">
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'document_type']}
                      rules={[{ required: true, message: 'Type' }]}
                    >
                      <Select placeholder="Type" style={{ width: 140 }}>
                        {documentTypes.map(dt => <Option key={dt.value} value={dt.value}>{dt.label}</Option>)}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'issue_date']}
                      label="Issue Date"
                      getValueProps={(value) => ({
                        value: value ? new Date(value).toISOString().split('T')[0] : ''
                      })}
                    >
                      <Input type="date" placeholder="Issue Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'expiry_date']}
                      label="Expiry Date"
                      rules={[{ required: true, message: 'Expiry date is required' }]}
                      getValueProps={(value) => ({
                        value: value ? new Date(value).toISOString().split('T')[0] : ''
                      })}
                    >
                      <Input type="date" placeholder="Expiry Date" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'document_file']}
                      valuePropName="fileList"
                      initialValue={[]}
                      getValueFromEvent={(e) => {
                        if (Array.isArray(e)) return e.slice(-1);
                        return e && e.fileList ? e.fileList.slice(-1) : [];
                      }}
                      rules={[
                        {
                          required: mode === 'add',
                          message: 'Document file is required'
                        },
                      ]}
                    >
                      <Upload
                        beforeUpload={() => false}
                        maxCount={1}
                        listType="picture"
                        accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                      >
                        <Button icon={<UploadOutlined />} size="small">File</Button>
                      </Upload>
                    </Form.Item>
                    <div style={{ paddingBottom: 24 }}>
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                      />
                    </div>
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Document
                  </Button>
                </Form.Item>
              </div>
            )}
          </Form.List>
        </Card>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>{mode === 'edit' ? 'Update Employee' : 'Create Employee'}</Button>
          <Button style={{ marginLeft: 8 }} onClick={onCancel}>Cancel</Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default SubcontractorEmployeeForm; 